import { useEffect, useRef, useState, type FormEvent } from "react";
import { brand } from "../config/brand";
import { LiveDisabledDisclaimer, RecoveryCodesOnce, TotpQr } from "../components/MfaUi";
import {
  beginSetup,
  confirmSetupTotp,
  type AuthSessionResult,
} from "../lib/auth";
import { readSetupTokenFromFragment } from "../lib/setup-token-fragment";

type SetupStep = "credentials" | "totp" | "recovery" | "done" | "invalid";

/** Begin failures the owner can correct without burning the in-memory setup token. */
const RECOVERABLE_SETUP_BEGIN_CODES = new Set([
  "SETUP_IDENTITY_MISMATCH",
  "SETUP_DISPLAY_NAME_REQUIRED",
  "PASSWORD_TOO_WEAK",
]);

function beginFailureMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case "SETUP_IDENTITY_MISMATCH":
      return "Email or display name does not match this setup token. Use the exact values bound at mint.";
    case "SETUP_DISPLAY_NAME_REQUIRED":
      return "Display name is required and must match the value bound to this setup token.";
    case "PASSWORD_TOO_WEAK":
      return "Password does not meet policy. Choose a stronger password and try again.";
    case "SETUP_INVALID":
      return "Setup token is invalid or expired. Re-open the custody setup URL, or request a new token.";
    default:
      return fallback;
  }
}

export function SetupPage({
  onNavigate,
  onAuthenticated,
}: {
  onNavigate: (path: string) => void;
  onAuthenticated: (session: AuthSessionResult) => void;
}) {
  const setupTokenRef = useRef<string | null>(null);
  /** Complements HttpOnly scc_setup cookie when cross-origin browsers block it. */
  const setupChallengeRef = useRef<string | null>(null);
  const [step, setStep] = useState<SetupStep>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [pendingSession, setPendingSession] = useState<AuthSessionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = readSetupTokenFromFragment();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${window.location.search}`
    );
    if (!token) {
      setupTokenRef.current = null;
      setStep("invalid");
      return;
    }
    setupTokenRef.current = token;
    return () => {
      setupTokenRef.current = null;
    };
  }, []);

  function clearSetupToken(): void {
    setupTokenRef.current = null;
  }

  async function onBegin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const setupToken = setupTokenRef.current;
    if (!setupToken) {
      setStep("invalid");
      return;
    }
    setSubmitting(true);
    try {
      const result = await beginSetup({
        setupToken,
        email,
        password,
        displayName: displayName.trim() || undefined,
      });
      if (!result.ok) {
        const message = beginFailureMessage(
          result.code,
          result.message || "Setup could not be started"
        );
        setError(message);
        // Keep the in-memory token for recoverable binding/password errors so the
        // owner can correct fields without re-opening the custody URL. Only burn
        // the token for terminal SETUP_INVALID / unknown failures.
        if (result.code && RECOVERABLE_SETUP_BEGIN_CODES.has(result.code)) {
          setStep("credentials");
          return;
        }
        clearSetupToken();
        setStep("invalid");
        return;
      }
      // Bootstrap URL token only needed for begin; confirm uses cookie + memory challenge.
      clearSetupToken();
      setupChallengeRef.current = result.setupChallengeToken ?? null;
      setOtpauthUri(result.otpauthUri);
      setStep("totp");
    } catch {
      // Network/parse failures: keep token so a retry does not require re-open.
      setError("Setup could not be started. Check connectivity and try again.");
      setStep("credentials");
    } finally {
      setSubmitting(false);
    }
  }

  async function onConfirmTotp(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await confirmSetupTotp(
        totpCode,
        setupChallengeRef.current ?? undefined
      );
      if (!result.ok) {
        if (result.code === "SETUP_INVALID") {
          setError(
            "Setup session expired or missing. Re-open the custody setup URL and begin again."
          );
        } else {
          setError(result.message);
        }
        return;
      }
      setupChallengeRef.current = null;
      setOtpauthUri(null);
      setPendingSession({
        user: result.user,
        session: result.session,
        csrfToken: result.csrfToken,
      });
      setRecoveryCodes(result.recoveryCodes);
      setStep("recovery");
    } catch {
      setError("Setup confirmation failed");
      setStep("invalid");
    } finally {
      setSubmitting(false);
    }
  }

  function onAcknowledgeRecovery() {
    const session = pendingSession;
    setRecoveryCodes(null);
    setPendingSession(null);
    setStep("done");
    if (session) onAuthenticated(session);
  }

  if (step === "invalid") {
    return (
      <div className="login-shell">
        <div className="login-card">
          <div className="sidebar-mark" aria-hidden="true" style={{ width: 42, height: 42 }}>
            {brand.logoText}
          </div>
          <div>
            <h1>Setup unavailable</h1>
            <p className="subtitle">{brand.productName}</p>
          </div>
          <LiveDisabledDisclaimer />
          <div className="login-disclaimer" role="alert">
            {error ??
              "A valid setup token is required. Open the custody fragment URL, or request a new bootstrap token. Preview bypass is not available."}
          </div>
          <button type="button" className="btn btn-primary" onClick={() => onNavigate("/login")}>
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <form
        className="login-card"
        onSubmit={step === "credentials" ? onBegin : step === "totp" ? onConfirmTotp : (e) => e.preventDefault()}
        style={step === "recovery" ? { width: "min(480px, 100%)" } : undefined}
      >
        <div className="sidebar-mark" aria-hidden="true" style={{ width: 42, height: 42 }}>
          {brand.logoText}
        </div>
        <div>
          <h1>
            {step === "credentials"
              ? "Owner setup"
              : step === "totp"
                ? "Enroll authenticator"
                : step === "recovery"
                  ? "Recovery codes"
                  : "Setup complete"}
          </h1>
          <p className="subtitle">{brand.productName}</p>
        </div>
        <LiveDisabledDisclaimer />

        {step === "credentials" ? (
          <>
            <div className="field">
              <label htmlFor="setup-email">Work email</label>
              <input
                id="setup-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="setup-password">Password</label>
              <input
                id="setup-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                required
                minLength={12}
              />
            </div>
            <div className="field">
              <label htmlFor="setup-display-name">Display name (required; must match token binding)</label>
              <input
                id="setup-display-name"
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={(ev) => setDisplayName(ev.target.value)}
                required
              />
            </div>
            {error ? (
              <div className="login-disclaimer" role="alert">
                {error}
              </div>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Starting…" : "Begin enrollment"}
            </button>
          </>
        ) : null}

        {step === "totp" && otpauthUri ? (
          <>
            <TotpQr otpauthUri={otpauthUri} />
            <div className="field">
              <label htmlFor="setup-totp">Authenticator code</label>
              <input
                id="setup-totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={8}
                value={totpCode}
                onChange={(ev) => setTotpCode(ev.target.value)}
                required
              />
            </div>
            {error ? (
              <div className="login-disclaimer" role="alert">
                {error}
              </div>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Confirming…" : "Confirm TOTP"}
            </button>
          </>
        ) : null}

        {step === "recovery" && recoveryCodes ? (
          <RecoveryCodesOnce codes={recoveryCodes} onContinue={onAcknowledgeRecovery} />
        ) : null}
      </form>
    </div>
  );
}
